import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toast';
import axiosInstance from '../../api/axiosInstance';
import '../../styles/all-workspaces.css';
import { formatStatusLabel } from '../../utils/formatStatusLabel';
import { ProjectTypeBadge } from '../common';
import { CreateProjectModal } from '../projects/Modals';
import { RtModal, RtModalField } from '../ui/RtModal';

const transformProject = (project) => ({
  id: project.project_id,
  name: project.project_name,
  description: project.description || '',
  type: project.project_type || 'None',
  status: project.status || 'active',
  updatedAt: project.updated_at,
});

const WorkspaceProjectSelectModal = ({ isOpen, workspace, onClose, onProjectSelect }) => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectType, setProjectType] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const resetCreateForm = () => {
    setProjectName('');
    setProjectDescription('');
    setProjectType('');
  };

  const fetchProjects = useCallback(async () => {
    if (!workspace?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await axiosInstance.get('/projects/list_project', {
        params: {
          page: 1,
          page_size: 100,
          pro_status: 'active',
          workspace_id: workspace.id,
        },
      });

      const projectList = (response?.projects || []).map(transformProject);
      setProjects(projectList);
    } catch (fetchError) {
      setError(fetchError?.response?.data?.message || 'Failed to load projects');
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, [workspace?.id]);

  useEffect(() => {
    if (isOpen && workspace?.id) {
      setSearchTerm('');
      setCreateProjectModalOpen(false);
      resetCreateForm();
      fetchProjects();
    }
  }, [isOpen, workspace?.id, fetchProjects]);

  const handleCloseCreateProjectModal = () => {
    if (isCreating) return;
    setCreateProjectModalOpen(false);
    resetCreateForm();
  };

  const handleCreateProject = async () => {
    if (!workspace?.id) {
      toast.error('Invalid workspace. Please try again.');
      return;
    }

    if (!projectName.trim() || !projectType) {
      toast.error('Project name and type are required');
      return;
    }

    try {
      setIsCreating(true);
      const response = await axiosInstance.post('/projects/add_project', {
        project_name: projectName.trim(),
        description: projectDescription,
        project_type: projectType,
        status: 'active',
        workspace_id: workspace.id,
      });

      if (response.status_code == 201) {
        // Success toast is shown by axiosInstance response interceptor
        setCreateProjectModalOpen(false);
        resetCreateForm();
        await fetchProjects();
      } else {
        toast.error(response?.message || 'Failed to create project');
      }
    } catch (createError) {
      console.error('Error creating project:', createError);
      toast.error(createError?.response?.data?.message || 'Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen || !workspace) return null;

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredProjects = normalizedSearch
    ? projects.filter((project) => project.name.toLowerCase().includes(normalizedSearch))
    : projects;

  // Hide select modal while create modal is open so CreateProjectModal is visible
  // without stacking overlays.
  if (createProjectModalOpen) {
    return (
      <CreateProjectModal
        toggleCreateProjectModal={handleCloseCreateProjectModal}
        projectName={projectName}
        setProjectName={setProjectName}
        projectDescription={projectDescription}
        setProjectDescription={setProjectDescription}
        projectType={projectType}
        setProjectType={setProjectType}
        handleCreateProject={handleCreateProject}
        loading={isCreating}
      />
    );
  }

  return (
    <RtModal
      open={isOpen}
      onClose={onClose}
      title="Select Project"
      subtitle={
        <>
          Choose a project in <strong>{workspace.workspace_name}</strong> to continue
        </>
      }
      size="lg"
      className="rt-modal--project-select workspace-modal project-select-modal"
      footer={
        <>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--primary"
            onClick={() => setCreateProjectModalOpen(true)}
          >
            Create Project
          </button>
        </>
      }
    >
      <RtModalField label="Search projects" htmlFor="project-search">
        <input
          id="project-search"
          type="text"
          placeholder="Search by project name"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </RtModalField>

      {error && (
        <div className="error-message project-select-error">
          <span>{error}</span>
        </div>
      )}

      {isLoading ? (
        <div className="project-select-loading">
          <div className="spinner" />
          <span>Loading projects...</span>
        </div>
      ) : filteredProjects.length > 0 ? (
        <div className="project-select-list rt-project-select-list">
          {filteredProjects.map((project) => (
            <button
              key={project.id}
              type="button"
              className="project-select-item"
              onClick={() => onProjectSelect(project)}
            >
              <div className="project-select-item-content">
                <span className="project-select-item-name">{project.name}</span>
                <span className="project-select-item-meta">
                  <ProjectTypeBadge type={project.type} /> · {formatStatusLabel(project.status)}
                </span>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          ))}
        </div>
      ) : (
        <div className="empty-state project-select-empty">
          <h3>No projects found</h3>
          <p>
            {searchTerm
              ? 'Try a different search term'
              : 'This workspace does not have any active projects yet'}
          </p>
        </div>
      )}
    </RtModal>
  );
};

export default WorkspaceProjectSelectModal;
